import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import DonationSuccess from './sections/DonationSuccess';
import { apiService } from '../services/api.service';
import '../styles.css';

interface LocationState {
  donationAmount?: number;
  donationType?: 'one-time' | 'monthly';
}

interface PayFastReturnParams {
  m_payment_id?: string;
  pf_payment_id?: string;
  payment_status?: string;
  item_name?: string;
  amount_gross?: string;
  custom_str1?: string;
}

const SuccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;
  
  const [donationDetails, setDonationDetails] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [emailAttempted, setEmailAttempted] = useState(false);
  
  // Get PayFast return parameters from URL
  const urlParams = new URLSearchParams(location.search);
  const payFastParams: PayFastReturnParams = {
    m_payment_id: urlParams.get('m_payment_id') || undefined,
    pf_payment_id: urlParams.get('pf_payment_id') || undefined,
    payment_status: urlParams.get('payment_status') || undefined,
    item_name: urlParams.get('item_name') || undefined,
    amount_gross: urlParams.get('amount_gross') || undefined,
    custom_str1: urlParams.get('custom_str1') || undefined,
  };

  // Get donation ID from URL params or state
  const donationId = urlParams.get('donation_id') || payFastParams.m_payment_id || payFastParams.custom_str1;
  
  const donationAmount = state?.donationAmount || parseFloat(payFastParams.amount_gross || '0');
  const donationType = state?.donationType || 'one-time';

  useEffect(() => {
    console.log('🔄 SuccessPage useEffect running with:', { donationId, emailAttempted });
    
    const fetchDonationDetails = async () => {
      if (donationId) {
        try {
          console.log('📋 Fetching donation details for:', donationId);
          const response = await apiService.getDonationById(donationId);
          if (response.success && response.data) {
            console.log('📊 Donation data:', { 
              donationId: response.data.donationId, 
              confirmationEmailSent: response.data.confirmationEmailSent,
              emailAttempted 
            });
            setDonationDetails(response.data);
            
            // If we have PayFast return parameters, update the donation status
            if (payFastParams.payment_status) {
              console.log('💳 Updating payment status with PayFast params');
              await apiService.updatePaymentStatus(donationId, {
                paymentId: payFastParams.pf_payment_id,
                paymentStatus: payFastParams.payment_status === 'COMPLETE' ? 'complete' : 'failed',
                transactionId: payFastParams.pf_payment_id
              });
            }

            // Check localStorage for email attempt (prevents duplicates across page reloads and React StrictMode)
            const emailAttemptKey = `email_attempted_${donationId}`;
            const localStorageAttempted = localStorage.getItem(emailAttemptKey) === 'true';
            
            console.log('📧 Email check:', { 
              emailSentInDB: response.data.confirmationEmailSent, 
              emailAttempted,
              localStorageAttempted,
              shouldSend: !response.data.confirmationEmailSent && !emailAttempted && !localStorageAttempted
            });
            
            if (!response.data.confirmationEmailSent && !emailAttempted && !localStorageAttempted) {
              console.log('📤 Attempting to send email...');
              setEmailAttempted(true); // Mark as attempted in state
              localStorage.setItem(emailAttemptKey, 'true'); // Mark as attempted in localStorage
              
              try {
                console.log('📧 Sending confirmation email...');
                const emailResponse = await fetch(`/api/donations/${donationId}/send-confirmation`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  }
                });

                const emailResult = await emailResponse.json();
                if (emailResult.success) {
                  console.log('✅ Confirmation email sent successfully');
                  // Clean up localStorage after successful send
                  setTimeout(() => {
                    localStorage.removeItem(emailAttemptKey);
                  }, 5000); // Remove after 5 seconds
                } else {
                  console.log('ℹ️ Email status:', emailResult.message);
                }
              } catch (emailError) {
                console.error('Error sending confirmation email:', emailError);
                // Don't fail the page load if email fails
              }
            } else {
              console.log('⏭️ Skipping email send - already sent or attempted');
            }
          }
        } catch (error) {
          console.error('Error fetching donation details:', error);
        }
      }
      setLoading(false);
    };

    fetchDonationDetails();
  }, [donationId]); // Only depend on donationId to prevent unnecessary re-runs

  const scrollToDonate = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/', { replace: true });
    // Small delay to ensure navigation completes before scrolling
    setTimeout(() => {
      document.getElementById('donate')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleMakeAnotherDonation = () => {
    navigate('/', { replace: true });
    // Small delay to ensure navigation completes before scrolling
    setTimeout(() => {
      document.getElementById('donate')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  if (loading) {
    return (
      <div className="page-container">
        <Navbar onDonateClick={scrollToDonate} />
        <div style={{ padding: '100px 0', textAlign: 'center' }}>
          <h2>Processing your donation...</h2>
          <p>Please wait while we confirm your payment.</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="page-container">
      <Navbar onDonateClick={scrollToDonate} />
      
      <DonationSuccess onDonateClick={scrollToDonate} />
      
      <section className="success-section" style={{ backgroundColor: 'white', padding: '80px 0' }}>
        <div className="container">
          <div className="success-page-content">
{/* Donation details block hidden
            {donationDetails && (
              <div style={{ marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <h3>Donation Details</h3>
                <p><strong>Donation ID:</strong> {donationDetails.donationId}</p>
                <p><strong>Amount:</strong> R{donationDetails.amount}</p>
                <p><strong>Type:</strong> {donationDetails.donationType}</p>
                <p><strong>Status:</strong> {donationDetails.donationStatus}</p>
                {payFastParams.pf_payment_id && (
                  <p><strong>PayFast Payment ID:</strong> {payFastParams.pf_payment_id}</p>
                )}
              </div>
            )}
            */}
            
            <h1 className="success-page-title">
              We are immensely grateful for your generosity. With your help, we are one step closer to achieving our goal and making a real impact on the lives of those affected by malaria.
            </h1>
            
            <p className="success-page-subtitle">
              Your donation will fund critical initiatives, provide resources, and bring us closer to a malaria-free future for southern Africa. Your support is saving lives, one contribution at a time.
            </p>

            <h2 className="success-page-section-title">
              Next Steps
            </h2>
            <p className="success-page-text">
              Stay connected with us to see the impact of your donation and receive updates on our mission's progress. Follow us on social media or subscribe to our newsletter to stay informed.
            </p>

            <h2 className="success-page-section-title">
              Receipt and Confirmation
            </h2>
            <p className="success-page-text">
              A confirmation email with the details of your donation has been sent to your provided email address. Please keep this for your records.
            </p>

            <h2 className="success-page-section-title">
              Appreciation
            </h2>
            <p className="success-page-text">
              Once again, thank you for your compassion and commitment to our cause. Your donation is making a difference. Together, we can create a brighter and healthier future for all.
            </p>

            <div className="success-page-buttons">
              <button
                onClick={handleMakeAnotherDonation}
                style={{
                  backgroundColor: 'var(--color-green)',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--color-background)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'var(--color-green)'}
              >
                Make Another Donation
              </button>
              
              <button
                onClick={() => navigate('/')}
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--color-heading)',
                  border: '2px solid var(--color-light-grey)',
                  padding: '15px 30px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-light-grey)';
                  e.currentTarget.style.borderColor = 'var(--color-grey)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = 'var(--color-light-grey)';
                }}
              >
                Return Home
              </button>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default SuccessPage;
